import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useAuth } from '../App';
import { format, differenceInDays } from 'date-fns';

function ExpiryWarning({ nextExpiry, balance }) {
  if (!nextExpiry || balance === 0) return null;
  const days = differenceInDays(new Date(nextExpiry), new Date());
  if (days > 30) return null;
  const urgent = days <= 7;
  return (
    <div className={`alert ${urgent ? 'alert-error' : 'alert-warning'}`} style={{ marginBottom: 0 }}>
      ⏰ {balance} points expire in {days} day{days !== 1 ? 's' : ''} — book a treatment before {format(new Date(nextExpiry), 'd MMM yyyy')}
    </div>
  );
}

export default function DashboardPage() {
  const { session } = useAuth();
  const [me, setMe]       = useState(null);
  const [wallet, setWallet] = useState({ balance: 0, nextExpiry: null });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks whether the person is actually signed in. The home screen is public
  // (guests can browse), so we must NOT render a wallet balance for a signed-out
  // visitor — auth.uid() is null server-side, so the wallet reads back as 0 and
  // looks like their points vanished. Instead we show a sign-in prompt.
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) { setSignedIn(false); setLoading(false); return; }
      setSignedIn(true);
      const uid = session.user.id;
      Promise.all([
        supabase.rpc('get_my_profile'),
        supabase.rpc('get_my_wallet_data'),
      ]).then(([profileRes, walletRes]) => {
        setMe({ profile: profileRes.data, email: session.user.email });
        const w = walletRes.data;
        setWallet({ balance: w?.balance || 0, nextExpiry: w?.nextExpiry || null });
      }).catch(() => {}).finally(() => setLoading(false));
    });
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const upcoming = bookings.filter(b => b.status === 'confirmed' && new Date(b.availability_slots?.starts_at) > new Date()).slice(0, 2);

  return (
    <div className="page">
      {/* Greeting */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Hi {me?.profile?.first_name || 'there'}</h1>
        <p style={{ color: '#FF6200', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.18em' }}>TRAIN · RECOVER · BREAKTHROUGH</p>
      </div>

      {/* Expiry warning */}
      {wallet && <ExpiryWarning nextExpiry={wallet.nextExpiry} balance={wallet.balance} />}

      {/* Wellness wallet — real balance for members, sign-in prompt for guests.
          Never show a "0" balance to a signed-out visitor: that reads as if
          their points disappeared, when they're simply not logged in. */}
      {signedIn ? (
        <div style={{ background: '#1A1A1A', borderRadius: 16, padding: 24, marginBottom: 16, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 120, height: 120, borderRadius: '50%', background: '#FF6200', opacity: 0.3 }} />
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#FF6200', letterSpacing: '0.08em', marginBottom: 6 }}>WELLNESS WALLET</div>
          <div style={{ fontSize: '3rem', fontWeight: 900, lineHeight: 1 }}>{wallet?.balance || 0}</div>
          <div style={{ fontSize: '0.9rem', color: '#aaa', marginTop: 4 }}>points available</div>
          {wallet?.nextExpiry && (
            <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 8 }}>
              Expires {format(new Date(wallet.nextExpiry), 'd MMM yyyy')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Link to="/wellness" className="btn btn-primary" style={{ flex: 1, fontSize: '0.85rem', padding: '10px 0' }}>Book Treatment</Link>
            <Link to="/wallet/packs" className="btn btn-secondary" style={{ flex: 1, fontSize: '0.85rem', padding: '10px 0', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>Buy Points</Link>
          </div>
        </div>
      ) : (
        <div style={{ background: '#1A1A1A', borderRadius: 16, padding: 24, marginBottom: 16, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 120, height: 120, borderRadius: '50%', background: '#FF6200', opacity: 0.3 }} />
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#FF6200', letterSpacing: '0.08em', marginBottom: 6 }}>WELLNESS WALLET</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.3 }}>Sign in to see your points</div>
          <div style={{ fontSize: '0.9rem', color: '#aaa', marginTop: 6 }}>Log in to view your balance and book treatments.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Link to="/login" className="btn btn-primary" style={{ flex: 1, fontSize: '0.85rem', padding: '10px 0' }}>Log In</Link>
            <Link to="/signup" className="btn btn-secondary" style={{ flex: 1, fontSize: '0.85rem', padding: '10px 0', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>Sign Up</Link>
          </div>
        </div>
      )}

      {/* Quick links */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
          { to: '/fitness',          label: 'Class Timetable',    photo: 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/class%20timetable.png' },
          { to: '/wellness',         label: 'Wellness Treatments', photo: 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/wellnesstreatments.jpeg' },
          { to: '/bookings',         label: 'My Bookings',     photo: 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/mybookings.png' },
          { to: '/wallet',           label: 'Points History',  photo: 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/dumbells.jpeg' },
        ].map(({ to, svg, label, photo }) => (
          photo ? (
            <Link key={to} to={to} style={{ textDecoration: 'none', borderRadius: 12, overflow: 'hidden', display: 'block', position: 'relative', height: 120,
              backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.65) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 10, left: 12, color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>{label}</div>
            </Link>
          ) : (
            <Link key={to} to={to} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, textDecoration: 'none' }}>
              {svg}
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</span>
            </Link>
          )
        ))}
      </div>

      {/* Upcoming bookings */}
      {upcoming.length > 0 && (
        <>
          <div className="section-title">Upcoming</div>
          {upcoming.map(b => (
            <div key={b.id} className="card" style={{ padding: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: 'var(--green-pale)', borderRadius: 10, padding: '10px 12px', textAlign: 'center', minWidth: 56 }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--green)', lineHeight: 1 }}>
                  {format(new Date(b.availability_slots.starts_at), 'd')}
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--green)', textTransform: 'uppercase' }}>
                  {format(new Date(b.availability_slots.starts_at), 'MMM')}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{b.treatments?.name}</div>
                <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>
                  {format(new Date(b.availability_slots.starts_at), 'EEEE h:mm aaa')}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Biohack promo */}
      <div style={{ background: '#1A1A1A', borderRadius: 16, overflow: 'hidden', marginTop: 8 }}>
        {/* Hero photo with overlay text */}
        <div style={{
          position: 'relative',
          height: 220,
          backgroundImage: 'url(https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/biohack.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.45) 50%, rgba(26,26,26,0.95) 100%)',
          }} />
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '20px' }}>
            <h3 style={{ color: '#fff', fontWeight: 900, fontSize: '1.5rem', letterSpacing: '0.02em', margin: 0 }}>THE BIOHACK</h3>
            <p style={{ color: '#e0e0e0', fontSize: '0.85rem', marginTop: 6, lineHeight: 1.5 }}>Two curated biohacking circuits — Gold (full, 3–4 hrs) or Silver (2 hrs). Everything sequenced for you.</p>
          </div>
        </div>
        {/* Pricing buttons */}
        <div style={{ padding: '14px 20px 20px' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#FF6200', letterSpacing: '0.16em', marginBottom: 10 }}>FEATURED EXPERIENCE</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/wellness/biohack?tier=gold" className="btn btn-primary" style={{ flex: 1, fontSize: '0.85rem' }}>Gold — from £249</Link>
            <Link to="/wellness/biohack?tier=silver" className="btn" style={{ flex: 1, fontSize: '0.85rem', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>Silver — from £165</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
